import { verificationLoader, verifiedIdLoader, vendorLoader } from '@/navigation/loaders/authLoader'
import { storeLoader } from '@/navigation/loaders/storeLoader'
import { taxComplianceLoader } from '@/navigation/loaders/salesTaxLoaders'
import { initiateQuery, getSearchParams } from '@/navigation/loaders/rtkQueryLoader'
import { PageSizeChoiceParam } from '@gearghost/api-schemas/_primitives'
import { lazyRoute } from '@/navigation/utils/lazyRoute'
import { PATHS, getDashboardRoutePath } from '@/navigation/paths'
import { inventoryListAddressOf } from '@/component/user-side/user-inventory/utils/inventoryListAddress'
import StoreProfileRedirect from '@/navigation/components/StoreProfileRedirect'
import { inventoryItemsApi } from '@/redux/api/inventoryItemsApi'
import { ordersApi } from '@/redux/api/ordersApi'

import { store } from '@/redux/store'

const P = PATHS.dashboard
const S = P.store

/**
 * Combined loader: auth check + data fetching.
 * Propagates any redirect Response from the auth loader.
 */
const inventoryLoader = async ({ request }) => {
  // First: the store's gate (auth, role, and the vendor terms; it also
  // prefetches the user profile). Pass `request` so a logged-out bounce
  // preserves this route as ?returnTo= for post-login return.
  const authResult = await storeLoader({ request })
  if (authResult instanceof Response) return authResult

  // Second: fetch inventory data. The page and sales-tax filter are read as
  // the page reads them (inventoryListAddress), so this prefetches the list
  // the page asks for. The search term rides the `q` URL param (the
  // app-wide search param, shared with the store-listings page via
  // useSearchInput), so a reloaded/shared search URL prefetches the right list.
  const params = getSearchParams(request)
  const { page, perPage, tax } = inventoryListAddressOf(params)
  const search = params.get('q') || ''

  const inventory = await initiateQuery(inventoryItemsApi, 'getInventoryItems', {
    page,
    search,
    limit: perPage,
    tax
  })

  return { inventory }
}

/**
 * Orders loader: auth check + initial orders fetch.
 * Reads current tab from Redux for initial fetch.
 */
const ordersLoader = async ({ request }) => {
  // Auth check (also prefetches user profile). Pass `request` so a logged-out
  // bounce preserves this route as ?returnTo= for post-login return.
  const authResult = await verificationLoader({ request })
  if (authResult instanceof Response) return authResult

  const { tab } = store.getState().auth

  const orders = await initiateQuery(ordersApi, 'getOrders', {
    type: tab || 'vendor',
    page: 1,
    // How many a page shows, as the address holds the pager's choice (the page's own perPage).
    limit: PageSizeChoiceParam.parse(getSearchParams(request).get('perPage'))
  })

  return { orders }
}

/**
 * Notifications loader: auth check only.
 * The notifications component handles its own data fetching via RTK Query.
 */
const notificationsLoader = async ({ request }) => {
  // Pass `request` so a logged-out bounce preserves this route as ?returnTo=.
  const authResult = await verificationLoader({ request })
  if (authResult instanceof Response) return authResult
  return null
}

/**
 * Dashboard routes - require authentication, use DashboardLayout (sidebar).
 * Each route has a title for the NavBar.
 */
export const dashboardRoutes = [
  // Messages
  {
    path: getDashboardRoutePath(P.messages),
    loader: verificationLoader,
    handle: { title: 'Messages' },
    lazy: lazyRoute(() => import('@/component/pages/messages'))
  },
  // Profile: the person's page, and for a vendor the store's too (profile
  // plan item 92).
  {
    path: getDashboardRoutePath(P.profile),
    loader: verificationLoader,
    handle: { title: 'Profile' },
    lazy: lazyRoute(() => import('@/component/user-side/user-user-profile'))
  },
  // Account Standing: the checklist of what gates the account, off the
  // profile's tab onto its own page (profile plan item 66).
  {
    path: getDashboardRoutePath(P.accountStanding),
    loader: verificationLoader,
    handle: { title: 'Account Standing' },
    lazy: lazyRoute(() => import('@/component/user-side/user-account-standing'))
  },
  // Orders
  {
    path: getDashboardRoutePath(P.orders),
    loader: ordersLoader,
    handle: { title: 'Orders' },
    lazy: lazyRoute(() => import('@/component/user-side/user-orders'))
  },
  // Calendar
  {
    path: getDashboardRoutePath(P.calendar),
    loader: verificationLoader,
    handle: { title: 'Calendar' },
    lazy: lazyRoute(() => import('@/component/user-side/user-calendar'))
  },
  {
    path: getDashboardRoutePath(P.orderDetails),
    loader: verificationLoader,
    handle: { title: 'Order Details' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/user-orders/order-detail')
    )
  },
  {
    path: getDashboardRoutePath(P.orderChangeItems),
    loader: verificationLoader,
    handle: { title: 'Add Items' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/user-orders/order-change/items')
    )
  },
  // Payment Methods
  {
    path: getDashboardRoutePath(P.paymentMethods),
    loader: verificationLoader,
    handle: { title: 'Payment Methods' },
    lazy: lazyRoute(() => import('@/component/user-side/user-payment-methods'))
  },
  // Notifications
  {
    path: getDashboardRoutePath(P.notifications),
    loader: notificationsLoader,
    handle: { title: 'Notifications' },
    lazy: lazyRoute(() => import('@/component/user-side/user-notifications'))
  },
  {
    path: getDashboardRoutePath(P.notificationSettings),
    loader: verificationLoader,
    handle: { title: 'Notification Settings' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/user-notifications-settings')
    )
  },
  // Insurance
  {
    path: getDashboardRoutePath(P.insurance),
    loader: verificationLoader,
    handle: { title: 'Insurance' },
    lazy: lazyRoute(() => import('@/component/user-side/user-insurance'))
  },
  // Spending
  {
    path: getDashboardRoutePath(P.spending),
    loader: verificationLoader,
    handle: { title: 'Spending' },
    lazy: lazyRoute(() => import('@/component/user-side/spending'))
  },
  // Become a Vendor: open to everyone since 2026-09-21. A new store waits
  // at CREATED until an admin approves it (the store approval gate), which
  // is what the production-build switch this route used to sit behind was
  // waiting for. It still takes a verified ID, as store management does: a
  // check Stripe is still reviewing is not enough (2026-10-22).
  {
    path: getDashboardRoutePath(P.becomeVendor),
    loader: verifiedIdLoader,
    handle: { title: 'Become a Vendor' },
    lazy: lazyRoute(() => import('@/component/user-side/user-become-vendor'))
  },

  // ===== Vendor Routes (require vendor role) =====
  //
  // The store's pages carry storeLoader: the vendor gate, then the store's
  // standing on GearGhost's vendor terms, which sends a store that has not
  // accepted them to Account Standing, where the New Vendor Terms banner
  // opens them (Decision 47). Account Standing is above, on the verification loader,
  // so a store sent there is never sent on again. Two routes keep the plain
  // vendorLoader: the old store-profile addresses, which only forward to
  // the Profile page.

  // Metrics (vendor dashboard)
  {
    path: getDashboardRoutePath(S.metrics),
    loader: storeLoader,
    handle: { title: 'Metrics' },
    lazy: lazyRoute(() => import('@/component/user-side/dashboard'))
  },
  // Listings
  {
    path: getDashboardRoutePath(S.listings),
    loader: storeLoader,
    handle: { title: 'Listings' },
    lazy: lazyRoute(() => import('@/component/user-side/user-store-listings'))
  },
  // The one-page listing form (overhaul plan item 44) for add and edit.
  {
    path: getDashboardRoutePath(S.addListing),
    loader: storeLoader,
    handle: { title: 'Add Listing' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/user-store-listings/listing-page/AddListing')
    )
  },
  {
    path: getDashboardRoutePath(S.editListing),
    loader: storeLoader,
    handle: { title: 'Edit Listing' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/user-store-listings/listing-page/EditListing')
    )
  },
  // Store Inventory
  {
    path: getDashboardRoutePath(S.inventory),
    loader: inventoryLoader,
    handle: { title: 'Inventory' },
    lazy: lazyRoute(() => import('@/component/user-side/user-inventory'))
  },
  {
    path: getDashboardRoutePath(S.addInventory),
    loader: storeLoader,
    handle: { title: 'Add Inventory' },
    lazy: lazyRoute(() => import('@/component/user-side/user-inventory/add'))
  },
  {
    path: getDashboardRoutePath(S.editInventory),
    loader: storeLoader,
    handle: { title: 'Edit Inventory' },
    lazy: lazyRoute(() => import('@/component/user-side/user-inventory/edit'))
  },
  // Tax Compliance: the receipts that show the tax paid on the store's gear
  // (#495, Decisions 14 and 27); one receipt's review, and the sign screen
  // for every receipt ready (#1272). Not found while an admin has the Sales
  // Tax switch off (taxComplianceLoader).
  {
    path: getDashboardRoutePath(S.taxes),
    loader: taxComplianceLoader,
    handle: { title: 'Tax Compliance' },
    lazy: lazyRoute(() => import('@/component/user-side/user-taxes'))
  },
  {
    path: getDashboardRoutePath(S.taxReview),
    loader: taxComplianceLoader,
    handle: { title: 'Review Receipts' },
    lazy: lazyRoute(() => import('@/component/user-side/user-taxes/review'))
  },
  {
    path: getDashboardRoutePath(S.taxSign),
    loader: taxComplianceLoader,
    handle: { title: 'Sign Receipts' },
    lazy: lazyRoute(() => import('@/component/user-side/user-taxes/sign'))
  },
  {
    path: getDashboardRoutePath(S.taxReceipt),
    loader: taxComplianceLoader,
    handle: { title: 'Receipt' },
    lazy: lazyRoute(() => import('@/component/user-side/user-taxes/receipt'))
  },
  // Availability (vendor block dates)
  {
    path: getDashboardRoutePath(S.availability),
    loader: storeLoader,
    handle: { title: 'Block Availability' },
    lazy: lazyRoute(() => import('@/component/user-side/vendor-availability')),
  },
  {
    path: getDashboardRoutePath(S.blockCart),
    loader: storeLoader,
    handle: { title: 'Block Cart' },
    lazy: lazyRoute(() => import('@/component/user-side/vendor-availability/block-cart')),
  },
  {
    path: getDashboardRoutePath(S.availabilityBreakdown),
    loader: storeLoader,
    handle: { title: 'Availability Breakdown' },
    lazy: lazyRoute(() =>
      import('@/component/user-side/vendor-availability/availability-breakdown')
    ),
  },
  // Store Documents: the rental agreement, pickup instructions, and order
  // notes on their own page (profile plan item 93).
  {
    path: getDashboardRoutePath(S.documents),
    loader: storeLoader,
    handle: { title: 'Store Documents' },
    lazy: lazyRoute(() => import('@/component/user-side/user-store-documents'))
  },
  // The store's old page URLs land on Profile, or on Store Documents when
  // `?tab=` names one of its tabs (items 92 and 93).
  {
    path: getDashboardRoutePath(S.profile),
    loader: vendorLoader,
    handle: { title: 'Profile' },
    element: <StoreProfileRedirect />
  },
  {
    path: getDashboardRoutePath(S.editProfile),
    loader: vendorLoader,
    handle: { title: 'Profile' },
    element: <StoreProfileRedirect />
  },
  // Stripe Connect
  {
    path: getDashboardRoutePath(S.revenue),
    loader: storeLoader,
    handle: { title: 'Stripe Connect' },
    lazy: lazyRoute(() => import('@/component/user-side/user-payouts'))
  }
]
